<script lang="ts">
  import { errorFocusStore } from '$lib/stores/errorFocus'

  /**
   * ErrorFocusController: Handles error card focus and scrolling
   * Single Responsibility: Manage focus state and auto-scroll to focused errors
   */

  interface Props {
    errorListContainer?: HTMLDivElement | null
  }

  let { 
    errorListContainer = $bindable(null)
  }: Props = $props()

  let lastScrollTime = 0
  let focusState = $state({ focusedErrorId: null, source: null, timestamp: 0 })

  // Subscribe to focus store
  $effect(() => {
    const unsubscribe = errorFocusStore.subscribe(state => {
      focusState = state
    })
    
    return unsubscribe
  })

  // Auto-scroll to error card when focused from editor
  $effect(() => {
    // Remove previous focus class from all cards
    if (errorListContainer) {
      errorListContainer.querySelectorAll('.card-focused-from-editor').forEach(el => {
        el.classList.remove('card-focused-from-editor')
      })
    }

    // Only scroll if focus came from editor and we have the error
    if (focusState.source === 'editor' && focusState.focusedErrorId &&
        focusState.timestamp > lastScrollTime && errorListContainer) {

      // Find the error card element
      const errorCard = errorListContainer.querySelector(
        `[data-error-id="${focusState.focusedErrorId}"]`
      ) as HTMLElement

      if (errorCard) {
        // Calculate scroll position to center the card
        const containerRect = errorListContainer.getBoundingClientRect()
        const cardRect = errorCard.getBoundingClientRect()
        const relativeTop = errorCard.offsetTop

        // Smooth scroll to center the card in view
        const targetScroll = relativeTop - (containerRect.height / 2) + (cardRect.height / 2)

        errorListContainer.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth'
        })

        // Add hover state to the card (no animation)
        errorCard.classList.add('card-focused-from-editor')
      }

      lastScrollTime = Date.now()
    }
  })

  // Export current focus state
  export function getFocusState() {
    return focusState
  }

  // Clear focus
  export function clearFocus() {
    errorFocusStore.clearFocus()
  }

  // Set focus from external source
  export function setFocus(errorId: string, source: string) {
    errorFocusStore.focusError(errorId, source)
  }
</script>